/* ════════════════════════════════════════════════════════════════════════
   TASK SWAP UTILS — kindness-first one-tap volunteering across cleaning
   and chore tasks. Local/front-end only.
   ════════════════════════════════════════════════════════════════════════ */

const AGE_RANK = { Adult: 3, Teen: 2, Child: 1, Baby: 0, Elder: 3, Pet: 0, Other: 2 };

function todayIso() { return new Date().toISOString().slice(0, 10); }

/* Required minimum age group for a task (read from either `age` or `ageSuitability`) */
function taskMinAge(task) {
  if (task?.age) return task.age;
  if (Array.isArray(task?.ageSuitability) && task.ageSuitability.length) {
    // Return strictest item in the list (lowest age rank = strictest? Actually higher rank = adult only)
    const ranks = task.ageSuitability.map(a => AGE_RANK[a] ?? 2);
    return Object.keys(AGE_RANK).find(k => AGE_RANK[k] === Math.max(...ranks)) || "Teen";
  }
  return "Teen";
}

/* Count of open tasks for a member across both task pools */
function loadFor(memberId, tasks = [], cleaningTasks = []) {
  const a = (tasks || []).filter(t => t.assignedTo === memberId && t.status !== "Done").length;
  const b = (cleaningTasks || []).filter(t => t.assignedTo === memberId && t.status !== "Done").length;
  return a + b;
}

function latestMood(memberId, moodCheckins = []) {
  const today = todayIso();
  return (moodCheckins || [])
    .filter(c => c.memberId === memberId && (c.createdAt || "").slice(0, 10) === today)
    .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""))[0] || null;
}

/* ── canSwapTask ───────────────────────────────────────────────────── */

export function canSwapTask({ task, newMember }) {
  if (!task || !newMember) return { ok: false, reason: "Missing data." };
  if (task.assignedTo === newMember.id) return { ok: false, reason: "Already yours." };
  if (newMember.ageGroup === "Baby" || newMember.ageGroup === "Pet") {
    return { ok: false, reason: "This family member can't take tasks." };
  }
  return { ok: true };
}

/* ── getSwapWarning ────────────────────────────────────────────────── */

const ENERGY_ORDER = { Low: 0, Medium: 1, High: 2 };

export function getSwapWarning({ task, newMember, state }) {
  const warnings = [];
  if (!task || !newMember) return warnings;

  const taskEnergy = task.energy || task.energyRequired || "Low";
  const taskMinutes = task.estimatedMinutes ?? null;
  const mood = latestMood(newMember.id, state?.moodCheckins);
  const load = loadFor(newMember.id, state?.tasks, state?.cleaningTasks);
  const memberAge = AGE_RANK[newMember.ageGroup] ?? 2;
  const requiredAge = AGE_RANK[taskMinAge(task)] ?? 2;

  // Age suitability — strong warning
  if (memberAge < requiredAge) {
    warnings.push({
      level: "strong",
      text: "This task may not be age-appropriate. Choose an adult or teen, or edit the task.",
    });
  }

  // Mood guardrails
  if (mood?.zone === "red" && ENERGY_ORDER[taskEnergy] >= 1) {
    warnings.push({
      level: "gentle",
      text: `${newMember.name} checked in as needing support today. A lower-energy task may be kinder.`,
    });
  }
  if (mood?.zone === "blue" && (ENERGY_ORDER[taskEnergy] >= 1 || (taskMinutes && taskMinutes > 15))) {
    warnings.push({
      level: "gentle",
      text: `${newMember.name} is low-energy today. Consider keeping the task short and gentle.`,
    });
  }

  // Workload guardrail
  if (load >= 5) {
    warnings.push({
      level: "gentle",
      text: `${newMember.name} already has a lot on today. This swap is still allowed, but you may want to keep it light.`,
    });
  }
  return warnings;
}

/* ── applyTaskSwap (state mutation helper) ─────────────────────────── */

export function buildSwapPatch({ task, newMemberId, reason, source = "manual-volunteer" }) {
  return {
    assignedTo: newMemberId,
    previousAssignedTo: task.assignedTo || null,
    swappedTo: newMemberId,
    swappedAt: new Date().toISOString(),
    swapReason: reason || "",
    swapSource: source,
  };
}

/* ── Reason chips ──────────────────────────────────────────────────── */

export const SWAP_REASON_CHIPS = [
  "I have time now",
  "They need a break",
  "I'm already nearby",
  "I want to help",
  "Better fit for me",
  "Other",
];

/* ── getTodaySwaps ─────────────────────────────────────────────────── */

export function getTodaySwaps(state) {
  const today = todayIso();
  const matchAll = [...(state?.tasks || []), ...(state?.cleaningTasks || [])];
  return matchAll.filter(t => t.swappedAt && (t.swappedAt || "").slice(0, 10) === today);
}

/* ── Help Out / Pending Swap helpers ───────────────────────────────── */

/**
 * Tasks that family members can volunteer for (open, has a current
 * assignee that isn't everyone/ARA, and isn't already done). Returns
 * a unified shape across household tasks + cleaning tasks.
 */
export function getSwappableTasks(state) {
  const members = state?.members || [];
  const memberIds = new Set(members.map(m => m.id));
  const out = [];

  (state?.tasks || []).forEach(t => {
    if (t.status === "Done" || t.status === "Paused") return;
    if (!t.assignedTo || !memberIds.has(t.assignedTo)) return;
    out.push({
      id: t.id,
      kind: "task",
      title: t.title,
      assignedTo: t.assignedTo,
      categoryOrRoom: t.category || "Task",
      route: "/home-os/chores",
      raw: t,
    });
  });

  (state?.cleaningTasks || []).forEach(t => {
    if (t.status === "Done") return;
    if (!t.assignedTo || !memberIds.has(t.assignedTo)) return;
    out.push({
      id: `cleaning-${t.id}`,
      kind: "cleaning",
      title: t.title,
      assignedTo: t.assignedTo,
      categoryOrRoom: t.room ? `Room · ${t.room}` : "Cleaning",
      route: "/home-os/cleaning",
      raw: t,
    });
  });

  return out;
}

export function getHelpNeededCount(state) {
  return getSwappableTasks(state).length;
}

/**
 * Rank swappable tasks by who could use backup most. Higher score
 * means a stronger "help out" candidate: assignee has high load,
 * checked in as needing support, or task is high-energy.
 */
export function getMostRelevantSwapOpportunities(state, limit = 3) {
  const swappables = getSwappableTasks(state);
  if (swappables.length === 0) return [];

  const scoreFor = (item) => {
    let score = 0;
    const t = item.raw;
    const assignee = (state?.members || []).find(m => m.id === item.assignedTo);
    if (!assignee) return score;
    const mood = latestMood(assignee.id, state?.moodCheckins);
    if (mood?.zone === "red")    score += 4;
    if (mood?.zone === "blue")   score += 2;
    if (mood?.zone === "yellow") score += 1;
    const load = loadFor(assignee.id, state?.tasks, state?.cleaningTasks);
    if (load >= 6) score += 3;
    else if (load >= 4) score += 2;
    else if (load >= 2) score += 1;
    const energy = t.energy || t.energyRequired;
    if (energy === "High") score += 2;
    else if (energy === "Medium") score += 1;
    if (t.priority === "urgent") score += 3;
    else if (t.priority === "high") score += 2;
    if (t.dueDate === todayIso()) score += 1;
    return score;
  };

  return swappables
    .map(item => ({ ...item, _score: scoreFor(item) }))
    .sort((a, b) => b._score - a._score)
    .slice(0, limit);
}

/**
 * Convenience for dashboard / wall display indicators. Returns a
 * payload with count + the top N items with all the bits needed to
 * render a friendly "Help Out" pill list.
 */
export function getPendingHelpItems(state, limit = 3) {
  const items = getMostRelevantSwapOpportunities(state, limit);
  const total = getHelpNeededCount(state);
  const members = state?.members || [];
  return {
    total,
    items: items.map(it => ({
      ...it,
      assignee: members.find(m => m.id === it.assignedTo) || null,
    })),
  };
}

/* ── getSwapInsight (for ARA noticed) ──────────────────────────────── */

export function getSwapInsight(state) {
  const swaps = getTodaySwaps(state);
  if (swaps.length === 0) return null;
  const last = swaps.sort((a, b) => (b.swappedAt || "").localeCompare(a.swappedAt || ""))[0];
  const members = state?.members || [];
  const taker = members.find(m => m.id === last.swappedTo);
  const giver = members.find(m => m.id === last.previousAssignedTo);
  if (taker && giver) {
    return {
      id: "swap-kindness",
      text: `${taker.name} took a task from ${giver.name}. Nice household teamwork.`,
      action: { label: "Open Chores", route: "/home-os/chores" },
    };
  }
  return {
    id: "swap-kindness-generic",
    text: "Someone helped rebalance the load today. Nice household teamwork.",
    action: { label: "Open Chores", route: "/home-os/chores" },
  };
}
